<template>
    <v-data-table
        v-if="photos != null"
        :headers="photoHeaders"
        :items="photoItems"
        :search="search"
        no-results-text="No photos."
        item-key="id"
        class="elevation-1"
        :items-per-page="25"
    >
        <template v-slot:top>
            <v-text-field v-model="search" label="Search" class="mx-4"></v-text-field>
        </template>
        <template v-slot:item.image="{ item }">
            <img class="photo" :src="item.imageUrl" alt="item.id" />
        </template>
        <template v-slot:item.viewS3="{ item }">
            <v-icon @click="openS3Console(item)">$pencil</v-icon>
        </template>
        <template v-slot:item.delete="{ item }" v-if="isAuthorized('Photos:Manage')">
            <v-icon @click="deletePhoto(item)">$delete</v-icon>
        </template>
    </v-data-table>
</template>
<script>
import { buildPhotosAPI } from "@/services/api.services.ts";
import { withLoadingScreen } from "@/lib/loading.js";
import { DateTime } from "luxon";
import {
    findTaskById,
    findTaskDayInTaskGroups,
    getTaskDisplayNameInTaskSchedule,
} from "@/lib/taskUtils.js";
import { mapGetters } from "vuex";

export default {
    props: {
        experiment: {
            type: Object,
            required: true,
        },
        protocol: {
            type: Object,
            required: true,
        },
        taskSchedule: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            photosAPI: null,
            photos: null,
            photoItems: null,
            search: "",
            photoHeaders: [
                { title: "Photo", value: "image", sortable: false },
                { title: "Entity Name", value: "thingName", sortable: true },
                { title: "Captured By", value: "capturedBy", sortable: true },
                { title: "Date", value: "capturedDateString", sortable: true },
                { title: "Task Day", value: "taskDay", sortable: true },
                { title: "Task", value: "task", sortable: true },
            ],
        };
    },
    async mounted() {
        this.photosAPI = buildPhotosAPI(this.experiment.code);
        await withLoadingScreen(async () => {
            await this.fetchPhotos();
            if (this.isAuthorized("Photos:Manage")) {
                this.photoHeaders.push(
                    {
                        title: "View S3",
                        value: "viewS3",
                        sortable: false,
                    },
                    {
                        title: "Delete",
                        value: "delete",
                        sortable: false,
                    }
                );
            }
        });
    },
    computed: {
        ...mapGetters(["isAuthorized"]),
    },
    methods: {
        async fetchPhotos() {
            this.photos = await this.photosAPI.fetchAll();
            this.photoItems = this.photos.map((photo) => {
                return {
                    ...photo,
                    taskDay: this.getTaskDay(photo),
                    capturedDateString: this.getDateString(photo),
                    task: this.getTaskName(photo),
                };
            });
        },
        getTaskDay(photo) {
            const taskId = photo.dataCollectionTaskId;
            if (taskId == null) {
                return "";
            }
            const task = findTaskById(taskId, this.taskSchedule.dataCollectionTasks);
            return findTaskDayInTaskGroups(
                task,
                this.taskSchedule.taskGroups,
                this.experiment.day0
            );
        },
        getTaskName(photo) {
            const taskId = photo.dataCollectionTaskId;
            if (taskId == null) {
                return "Experiment";
            }
            const task = findTaskById(taskId, this.taskSchedule.dataCollectionTasks);
            return getTaskDisplayNameInTaskSchedule(task, this.taskSchedule, this.experiment.day0);
        },
        getDateString(photo) {
            const dateTime = DateTime.fromISO(photo.capturedDatetime, {
                zone: this.experiment.siteTimeZone,
            });
            return dateTime.toLocaleString(DateTime.DATETIME_MED);
        },
        async deletePhoto(photo) {
            if (confirm("Are you sure you want to delete this photo?")) {
                await withLoadingScreen(async () => {
                    await this.photosAPI.delete(photo.id);
                    await this.fetchPhotos();
                });
            }
        },
        openS3Console(photo) {
            window.open(photo.s3ConsoleUrl, "_blank");
        },
    },
};
</script>
<style scoped>
.photo {
    width: 100px;
    height: 100px;
}
</style>
